import { useEffect, useRef } from 'react'
import type { PopoverActions } from '@mui/material'

import { useChromeTransitioning } from 'config'

/**
 * ── KEEPING A SIDEBAR MENU WHERE IT BELONGS WHILE THE DRAWER IS STILL MOVING ──────
 *
 * Every placement above is measured from the drawer's right edge, and that edge MOVES:
 * `openedMixin`/`closedMixin` transition `width` over 225ms (80 -> 288). MUI's
 * `Popover` reads `anchorEl.getBoundingClientRect()` ONCE, when it opens, and never
 * again — so a menu that opens mid-transition is positioned against a width that no
 * longer exists a moment later.
 *
 * That is not a rare race. These menus open on hover intent, and the very hover that
 * opens them is the hover that starts the drawer expanding, so the two run together by
 * construction. Measured in the browser, same menu, same trigger:
 *
 *   | sidebar state when the menu opened | menu left |
 *   |------------------------------------|-----------|
 *   | mid-expand                         | 90   (anchor right 81  = 80 collapsed + 1px border) |
 *   | settled at 288                     | 296  (anchor right 287) |
 *
 * 206px apart, and which one you get depends on where the pointer came from. Moving
 * between the trigger and the menu therefore appears to shove the menu sideways.
 *
 * ── Why this signal and not a `transitionend` listener ───────────────────────────
 * `useChromeTransitioning` already exists for exactly this question (VRG-512): the
 * sidebar announces the width is moving and flips the store back when it settles. A
 * `transitionend` listener on `.MuiDrawer-paper` would need the element, the
 * `propertyName === 'width'` filter, and its own teardown, to learn the same fact that
 * is already published. Reusing it also means a future chrome transition that is not
 * the drawer gets this for free.
 *
 * Repositioning at the END rather than tracking through: the reposition lands ~25ms
 * after the menu appears (200ms open delay vs 225ms transition), which is below the
 * threshold where it reads as a move. Tracking continuously would need a rAF loop, and
 * rAF does not run in the preview pane — a fix nobody can verify there is worse than a
 * fix that jumps imperceptibly once.
 *
 * Pass the returned ref to the menu as `action={...}`; `Menu` forwards it to `Popover`.
 */
export const useChromeSettledPosition = (open: boolean) => {
  const actionRef = useRef<PopoverActions | null>(null)
  const chromeTransitioning = useChromeTransitioning()

  useEffect(() => {
    // Only once the width has SETTLED. Repositioning mid-transition would just capture
    // another intermediate value, which is the bug rather than the fix.
    if (open && !chromeTransitioning) actionRef.current?.updatePosition()
  }, [open, chromeTransitioning])

  return actionRef
}
